import { useState, useEffect } from 'react';
import api from '../../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { formatAdolescenteNome } from '../../utils/adolescenteNome';
import MotivosMbaSelector from './MotivosMbaSelector';

const COR_PADRAO_CARD = '#FFFFFF';
const CARD_COR_OPTIONS = [
  { cor: '#FFFFFF', label: 'Branco Puro' },
  { cor: '#D0E2FF', label: 'Azul Executivo' },
  { cor: '#BDEFFF', label: 'Azul Glacial' },
  { cor: '#C1F2E5', label: 'Acqua' },
  { cor: '#E2F2AF', label: 'Lima Suave' },
  { cor: '#FFECB3', label: 'Amarelo Solar' },
  { cor: '#FFD0A5', label: 'Laranja Pêssego' },
  { cor: '#FFCAD1', label: 'Rosa Flamingo' },
  { cor: '#E9D5FF', label: 'Violeta Real' },
  { cor: '#CBD2F9', label: 'Indigo Pastel' },
  { cor: '#C6D8D3', label: 'Cinza Sálvia' },
  { cor: '#EBDAC2', label: 'Café com Leite' },
];

const obterMotivosMbaIds = (movimentacao) => {
  const idsDaResposta = movimentacao?.motivos_mba_ids;
  if (Array.isArray(idsDaResposta) && idsDaResposta.length > 0) {
    return idsDaResposta.map(Number);
  }

  const motivos = movimentacao?.motivos_mba;
  if (Array.isArray(motivos) && motivos.length > 0) {
    return motivos.map((motivo) => Number(motivo.id));
  }

  return movimentacao?.motivo_mba?.id ? [Number(movimentacao.motivo_mba.id)] : [];
};

const getSwatchCheckColor = (hexColor) => {
  const hex = String(hexColor || '').replace('#', '');
  if (hex.length !== 6) return '#0f172a';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 168 ? '#0f172a' : '#ffffff';
};

function MovimentacaoResumoModal({ isOpen, onClose, movimentacaoId, theme = 'dark', onDeleteSuccess }) {
  const { hasPermission, hasAnyPermission } = useAuth();
  
  const canEdit = hasAnyPermission(['movimentacoes.create', 'movimentacoes.edit']);
  const canViewOnly = !canEdit && hasPermission('movimentacoes.view');
  const isReadOnly = !canEdit;
  const canReopen = hasPermission('movimentacoes.reopen');
  const canDelete = hasPermission('movimentacoes.delete');

  const [loading, setLoading] = useState(true);
  const [movimentacao, setMovimentacao] = useState(null);
  const [decisoes, setDecisoes] = useState([]);
  const [situacoes, setSituacoes] = useState([]);
  const [meiosSaida, setMeiosSaida] = useState([]);
  const [meiosEntrada, setMeiosEntrada] = useState([]);
  const [motivosMba, setMotivosMba] = useState([]);

  const [novaSituacaoId, setNovaSituacaoId] = useState('');
  const [registrandoSituacao, setRegistrandoSituacao] = useState(false);

  const [motivosMbaIds, setMotivosMbaIds] = useState([]);
  const [salvandoMotivoMba, setSalvandoMotivoMba] = useState(false);

  const [meioEntradaId, setMeioEntradaId] = useState('');
  const [salvandoMeioEntrada, setSalvandoMeioEntrada] = useState(false);
  const [showConfirmMeioEntrada, setShowConfirmMeioEntrada] = useState(false);
  const [pendingMeioEntradaId, setPendingMeioEntradaId] = useState(null);

  const [decisaoId, setDecisaoId] = useState('');
  const [decisaoMbaId, setDecisaoMbaId] = useState('');
  const [decisaoFlagranteId, setDecisaoFlagranteId] = useState('');
  const [salvandoDecisao, setSalvandoDecisao] = useState(false);

  const [meioSaidaId, setMeioSaidaId] = useState('');
  const [finalizando, setFinalizando] = useState(false);
  const [reabrindo, setReabrindo] = useState(false);
  const [cancelandoEntrada, setCancelandoEntrada] = useState(false);
  const [showConfirmCancelarEntrada, setShowConfirmCancelarEntrada] = useState(false);

  const [cardCor, setCardCor] = useState(COR_PADRAO_CARD);
  const [salvandoCor, setSalvandoCor] = useState(false);

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen && movimentacaoId) {
      fetchData();
    }
  }, [isOpen, movimentacaoId]);

  // Limpar mensagem de sucesso após 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const syncMovimentacaoState = (mov) => {
    if (!mov) return;
    setMovimentacao(mov);
    setNovaSituacaoId(mov?.situacao_atual?.id ? String(mov.situacao_atual.id) : '');
    setMeioEntradaId(mov?.meio_entrada?.id ? String(mov.meio_entrada.id) : '');
    setDecisaoId(mov?.decisao?.id ? String(mov.decisao.id) : '');
    setDecisaoMbaId(mov?.decisao_mba?.id ? String(mov.decisao_mba.id) : '');
    setDecisaoFlagranteId(mov?.decisao_flagrante?.id ? String(mov.decisao_flagrante.id) : '');
    setMeioSaidaId(mov?.meio_saida?.id ? String(mov.meio_saida.id) : '');
    setMotivosMbaIds(obterMotivosMbaIds(mov));
    setCardCor(mov?.card_cor || COR_PADRAO_CARD);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Buscar movimentação (obrigatória)
      const movResp = await api.get(`/movimentacoes/${movimentacaoId}`);
      const mov = movResp.data?.data || null;
      setMovimentacao(mov);

      // Buscar dados auxiliares (opcional - pode falhar por falta de permissão)
      const [decResp, sitResp, msResp, meResp, motivoMbaResp] = await Promise.allSettled([
        api.get('/decisoes', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        }),
        api.get('/situacoes'),
        api.get('/meios-saida', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        }),
        api.get('/meios-entrada', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        }),
        api.get('/motivos-mba', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        }),
      ]);

      // Processar decisões (se permitido)
      if (decResp.status === 'fulfilled') {
        setDecisoes(decResp.value.data?.data || []);
      } else if (decResp.reason?.kind === 'permission') {
        console.info('[MovimentacaoResumoModal] Sem permissão para carregar decisões');
        setDecisoes([]);
      }

      // Processar situações (se permitido)
      if (sitResp.status === 'fulfilled') {
        const listaSituacoes = (sitResp.value.data?.data || []).filter(s => s.tipo === 'movimentacao');
        setSituacoes(listaSituacoes);
      } else if (sitResp.reason?.kind === 'permission') {
        console.info('[MovimentacaoResumoModal] Sem permissão para carregar situações');
        setSituacoes([]);
      }

      // Processar meios de saída (se permitido)
      if (msResp.status === 'fulfilled') {
        setMeiosSaida(msResp.value.data?.data || []);
      } else if (msResp.reason?.kind === 'permission') {
        console.info('[MovimentacaoResumoModal] Sem permissão para carregar meios de saída');
        setMeiosSaida([]);
      }

      // Processar meios de entrada (se permitido)
      if (meResp.status === 'fulfilled') {
        setMeiosEntrada(meResp.value.data?.data || []);
      } else if (meResp.reason?.kind === 'permission') {
        console.info('[MovimentacaoResumoModal] Sem permissão para carregar meios de entrada');
        setMeiosEntrada([]);
      }

      // Processar motivos MBA (se permitido)
      if (motivoMbaResp.status === 'fulfilled') {
        setMotivosMba(motivoMbaResp.value.data?.data || []);
      } else if (motivoMbaResp.reason?.kind === 'permission') {
        console.info('[MovimentacaoResumoModal] Sem permissão para carregar motivos MBA');
        setMotivosMba([]);
      }

      // Inicializar estados
      setNovaSituacaoId(mov?.situacao_atual?.id ? String(mov.situacao_atual.id) : '');
      setMeioEntradaId(mov?.meio_entrada?.id ? String(mov.meio_entrada.id) : '');
      setDecisaoId(mov?.decisao?.id ? String(mov.decisao.id) : '');
      setDecisaoMbaId(mov?.decisao_mba?.id ? String(mov.decisao_mba.id) : '');
      setDecisaoFlagranteId(mov?.decisao_flagrante?.id ? String(mov.decisao_flagrante.id) : '');
      setMeioSaidaId(mov?.meio_saida?.id ? String(mov.meio_saida.id) : '');
      setMotivosMbaIds(obterMotivosMbaIds(mov));
      setCardCor(mov?.card_cor || COR_PADRAO_CARD);
    } catch (err) {
      console.error('Erro ao carregar dados da movimentação:', err);
      setError('Não foi possível carregar os dados da movimentação.');
    } finally {
      setLoading(false);
    }
  };

  const isMBA = Number(movimentacao?.meio_entrada?.id) === 3;
  const isFlagranteMBA = Number(movimentacao?.meio_entrada?.id) === 4;
  const motivoMbaObrigatorio = isMBA || isFlagranteMBA;
  const finalizada = Boolean(movimentacao?.finalizada);
  const dataMovimentacao = (movimentacao?.finalizada ? movimentacao?.finalizada_at : movimentacao?.created_at)
    ? new Date(movimentacao.finalizada ? movimentacao.finalizada_at : movimentacao.created_at)
    : null;
  const inicioHoje = new Date();
  inicioHoje.setHours(0, 0, 0, 0);
  const dataMovimentacaoDia = dataMovimentacao
    ? new Date(dataMovimentacao.getFullYear(), dataMovimentacao.getMonth(), dataMovimentacao.getDate())
    : null;
  const isPlantaoAnterior = !finalizada && dataMovimentacaoDia && dataMovimentacaoDia < inicioHoje;
  const motivosMbaDaMovimentacao = Array.isArray(movimentacao?.motivos_mba) && movimentacao.motivos_mba.length > 0
    ? movimentacao.motivos_mba
    : (movimentacao?.motivo_mba?.motivo ? [movimentacao.motivo_mba] : []);
  const hasMotivoMba = motivosMbaDaMovimentacao.length > 0;

  // Verificar se tem decisão informada
  const temDecisao = isFlagranteMBA
    ? Boolean(movimentacao?.decisao_mba?.id && movimentacao?.decisao_flagrante?.id)
    : Boolean(movimentacao?.decisao?.id);

  // Lista de atendimentos com status
  const atendimentos = [
    { nome: 'Promotoria', dados: movimentacao?.atendimento_promotoria },
    { nome: 'Defensoria', dados: movimentacao?.atendimento_defensoria },
    { nome: 'Saúde', dados: movimentacao?.atendimento_saude },
    { nome: 'Educação', dados: movimentacao?.atendimento_educacao },
    { nome: 'Especialistas', dados: movimentacao?.atendimento_especialistas },
  ];

  // Pode finalizar se tem decisão E meio de saída E (se MBA obrigatório, tem motivo MBA)
  const podeFinalizar = temDecisao && meioSaidaId && (!motivoMbaObrigatorio || motivosMbaIds.length > 0) && !finalizada;
  const conteudoModalCarregado = !loading && !error && Boolean(movimentacao);

  const registrarSituacao = async (novoValor) => {
    if (!novoValor || Number(novoValor) === Number(movimentacao?.situacao_atual?.id)) return;
    setRegistrandoSituacao(true);
    setError(null);
    setSuccessMessage('');
    try {
      const resp = await api.post(`/movimentacoes/${movimentacaoId}/situacoes`, {
        situacao_id: Number(novoValor) 
      });
      syncMovimentacaoState(resp?.data?.data || null);
      setSuccessMessage('Situação alterada com sucesso');
    } catch (err) {
      console.error('Erro ao salvar situação:', err);
      setError('Falha ao salvar a situação.');
    } finally {
      setRegistrandoSituacao(false);
    }
  };

  const handleMeioEntradaChange = (novoValor) => {
    if (!novoValor || Number(novoValor) === Number(movimentacao?.meio_entrada?.id)) return;
    // Mostrar modal de confirmação antes de salvar
    setPendingMeioEntradaId(novoValor);
    setShowConfirmMeioEntrada(true);
  };

  const confirmarMeioEntrada = async () => {
    if (!pendingMeioEntradaId) return;
    setSalvandoMeioEntrada(true);
    setError(null);
    setSuccessMessage('');
    setShowConfirmMeioEntrada(false);
    try {
      const resp = await api.patch(`/movimentacoes/${movimentacaoId}/meio-entrada`, {
        meio_entrada_id: Number(pendingMeioEntradaId),
      });
      syncMovimentacaoState(resp?.data?.data || null);
      setSuccessMessage('Meio de entrada alterado com sucesso. Decisões incompatíveis foram limpas automaticamente.');
    } catch (err) {
      console.error('Erro ao salvar meio de entrada:', err);
      setError(err.response?.data?.message || 'Falha ao salvar o meio de entrada.');
      // Reverter select para o valor original
      setMeioEntradaId(movimentacao?.meio_entrada?.id ?? '');
    } finally {
      setSalvandoMeioEntrada(false);
      setPendingMeioEntradaId(null);
    }
  };

  const cancelarMeioEntrada = () => {
    setShowConfirmMeioEntrada(false);
    setPendingMeioEntradaId(null);
    // Reverter select para o valor original
    setMeioEntradaId(movimentacao?.meio_entrada?.id ?? '');
  };

  const salvarDecisao = async (decisaoSimples = null, flagrante = null, mba = null) => {
    setSalvandoDecisao(true);
    setError(null);
    setSuccessMessage('');
    try {
      if (isFlagranteMBA) {
        // Usar endpoint específico que atualiza decisões SEM finalizar
        const resp = await api.patch(`/movimentacoes/${movimentacaoId}/decisao`, {
          decisao_flagrante_id: flagrante ? Number(flagrante) : null,
          decisao_mba_id: mba ? Number(mba) : null,
        });
        syncMovimentacaoState(resp?.data?.data || null);
        setSuccessMessage('Decisões alteradas com sucesso');
      } else {
        // Usar endpoint específico que atualiza decisão SEM finalizar
        const resp = await api.patch(`/movimentacoes/${movimentacaoId}/decisao`, {
          decisao_id: decisaoSimples ? Number(decisaoSimples) : null,
        });
        syncMovimentacaoState(resp?.data?.data || null);
        setSuccessMessage('Decisão alterada com sucesso');
      }
    } catch (err) {
      console.error('Erro ao salvar decisão:', err);
      setError('Falha ao salvar a decisão.');
    } finally {
      setSalvandoDecisao(false);
    }
  };

  const finalizarMovimentacao = async () => {
    if (!podeFinalizar) return;
    setFinalizando(true);
    setError(null);
    setSuccessMessage('');
    try {
      const resp = await api.post(`/movimentacoes/${movimentacaoId}/finalizar`, {
        meio_saida_id: Number(meioSaidaId),
      });
      syncMovimentacaoState(resp?.data?.data || null);
      setSuccessMessage('Movimentação finalizada com sucesso!');
      // Fechar modal após 2 segundos
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Erro ao finalizar movimentação:', err);
      setError(err.response?.data?.message || 'Falha ao finalizar a movimentação.');
    } finally {
      setFinalizando(false);
    }
  };

  const reabrirMovimentacao = async () => {
    if (!finalizada || !canReopen) return;
    setReabrindo(true);
    setError(null);
    setSuccessMessage('');
    try {
      const resp = await api.post(`/movimentacoes/${movimentacaoId}/reabrir`);
      const mov = resp?.data?.data;
      if (mov) {
        syncMovimentacaoState(mov);
      } else {
        setMovimentacao((prev) =>
          prev
            ? {
                ...prev,
                finalizada: false,
                finalizada_at: null,
              }
            : prev
        );
      }
      setSuccessMessage('Movimentação reaberta com sucesso!');
    } catch (err) {
      console.error('Erro ao reabrir movimentação:', err);
      setError(err.response?.data?.message || 'Falha ao reabrir a movimentação.');
    } finally {
      setReabrindo(false);
    }
  };

  const abrirConfirmacaoCancelarEntrada = () => {
    if (finalizada || !canDelete) return;
    setShowConfirmCancelarEntrada(true);
    setError(null);
    setSuccessMessage('');
  };

  const fecharConfirmacaoCancelarEntrada = () => {
    if (cancelandoEntrada) return;
    setShowConfirmCancelarEntrada(false);
  };

  const confirmarCancelarEntrada = async () => {
    if (!movimentacaoId || finalizada || !canDelete || cancelandoEntrada) return;
    setCancelandoEntrada(true);
    setError(null);
    setSuccessMessage('');
    try {
      await api.delete(`/movimentacoes/${movimentacaoId}`);
      setShowConfirmCancelarEntrada(false);
      onClose();
      if (typeof onDeleteSuccess === 'function') {
        onDeleteSuccess();
      }
    } catch (err) {
      console.error('Erro ao excluir movimentação:', err);
      setError(err.response?.data?.message || 'Falha ao excluir a movimentação.');
      setShowConfirmCancelarEntrada(false);
    } finally {
      setCancelandoEntrada(false);
    }
  };

  const salvarCardCor = async (cor) => {
    setSalvandoCor(true);
    setError(null);
    setSuccessMessage('');
    try {
      const resp = await api.patch(`/movimentacoes/${movimentacaoId}/card-cor`, {
        card_cor: cor || null,
      });
      syncMovimentacaoState(resp?.data?.data || null);
      setSuccessMessage('Cor do card atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar cor do card:', err);
      setError('Falha ao salvar a cor do card.');
    } finally {
      setSalvandoCor(false);
    }
  };

  const salvarMotivosMba = async (novosIds) => {
    const idsNormalizados = Array.from(new Set(novosIds.map(Number))).filter((id) => id > 0);
    if (JSON.stringify([...idsNormalizados].sort()) === JSON.stringify([...motivosMbaIds].sort())) return;

    setMotivosMbaIds(idsNormalizados);
    setSalvandoMotivoMba(true);
    setError(null);
    setSuccessMessage('');
    try {
      const resp = await api.patch(`/movimentacoes/${movimentacaoId}/motivo-mba`, {
        motivos_mba_ids: idsNormalizados,
      });
      syncMovimentacaoState(resp?.data?.data || null);
      setSuccessMessage('Motivo MBA atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar motivo MBA:', err);
      setError('Falha ao salvar o motivo MBA.');
    } finally {
      setSalvandoMotivoMba(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme} dashboard-themed-modal-no-overlay`}
        style={{ display: 'block' }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
              <div className="modal-header flex-column align-items-stretch pb-2">
                <div className="d-flex w-100 justify-content-between align-items-center">
                  <div style={{ width: '36px' }}></div>
                  <h5 className="modal-title text-center mb-0 flex-grow-1" style={{ fontSize: 'calc(1.5rem - 1px)' }}>
                    Resumo da Movimentação
                  </h5>
                  <button
                    type="button"
                    className="btn p-0 d-inline-flex align-items-center justify-content-center"
                    onClick={onClose}
                    aria-label="Fechar"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '5px',
                      border: '1px solid rgba(148, 214, 164, 0.55)',
                      background: theme === 'light'
                        ? 'linear-gradient(135deg, rgba(148, 214, 164, 0.18), rgba(148, 214, 164, 0.10))'
                        : 'linear-gradient(135deg, rgba(148, 214, 164, 0.16), rgba(148, 214, 164, 0.08))',
                      color: theme === 'light' ? '#2f4b66' : '#c7f0d1',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <i className="bi bi-x-lg" style={{ fontSize: '14px' }} />
                  </button>
                </div>
                <div
                  className="w-100 mt-2 mb-2"
                  style={{
                    height: '1px',
                    background: 'linear-gradient(90deg, rgba(148, 214, 164, 0.08) 0%, #94D6A4 50%, rgba(148, 214, 164, 0.08) 100%)',
                  }}
                />
                <div className="w-100 d-flex flex-wrap flex-md-nowrap align-items-start align-items-md-center gap-2">
                  <strong className="flex-grow-1 text-truncate" style={{ fontSize: 'calc(1rem + 2px)' }}>
                    {formatAdolescenteNome(movimentacao?.adolescente)} - ID Jornada: {movimentacao?.adolescente?.jornada_id || '-'}
                  </strong>
                  <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 ms-md-auto text-secondary">
                    <span className="d-inline-flex align-items-center margin-right-15">
                      <strong style={{ fontSize: 'calc(1rem + 2px)' }}>
                      <i className="bi bi-calendar3 me-1" />
                      {dataMovimentacao ? dataMovimentacao.toLocaleDateString('pt-BR') : '—'}
                      </strong>
                    </span>
                    {isPlantaoAnterior && (
                      <span className="d-inline-flex align-items-center">
                        <strong style={{ fontSize: 'calc(1rem + 2px)' }}>
                        <i className="bi bi-clock-history me-1" />
                        Plantão anterior
                        </strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>

            <div className="modal-body">
              {loading ? (
                <LoadingSpinner text="Carregando..." />
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <>
                  {/* Mensagem de Sucesso */}
                  {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                      <i className="bi bi-check-circle-fill me-2"></i>
                      {successMessage}
                    </div>
                  )}

                  {/* Informações Básicas */}
                  <div className="mb-4">
                    <div className="row">
                      <div className={`col-md-${hasMotivoMba ? '4' : '6'} mb-1`}>
                        <small className="text-secondary d-block mb-0" style={{ fontSize: '0.95rem' }}>Meio de Entrada</small>
                        <strong style={{ fontSize: '1.08rem', lineHeight: 1.2 }}>{movimentacao?.meio_entrada?.nome || '—'}</strong>
                      </div>
                      {/* Motivos MBA */}
                      {hasMotivoMba && (
                        <div className="col-md-4 mb-1">
                          <small className="text-secondary d-block mb-0" style={{ fontSize: '0.95rem' }}>Motivos MBA</small>
                          <strong style={{ fontSize: '1.08rem', lineHeight: 1.2 }}>
                            {motivosMbaDaMovimentacao.map((motivo) => motivo.motivo).join(', ')}
                          </strong>
                        </div>
                      )}

                      <div className={`col-md-${hasMotivoMba ? '4' : '6'} mb-1`}>
                        <small className="text-secondary d-block mb-0" style={{ fontSize: '0.95rem' }}>Situação Atual</small>
                        <strong style={{ fontSize: '1.08rem', lineHeight: 1.2 }}>{movimentacao?.situacao_atual?.nome || '—'}</strong>
                      </div>

                      {/* Decisões para visualização */}
                      {canViewOnly && (
                        <>
                          {!isFlagranteMBA ? (
                            <div className="col-md-6 mb-2">
                              <small className="text-secondary d-block">Decisão</small>
                              <strong>{movimentacao?.decisao?.nome || '—'}</strong>
                            </div>
                          ) : (
                            <>
                              <div className="col-md-6 mb-2">
                                <small className="text-secondary d-block">Decisão Flagrante</small>
                                <strong>{movimentacao?.decisao_flagrante?.nome || '—'}</strong>
                              </div>
                              <div className="col-md-6 mb-2">
                                <small className="text-secondary d-block">Decisão MBA</small>
                                <strong>{movimentacao?.decisao_mba?.nome || '—'}</strong>
                              </div>
                            </>
                          )}
                        </>
                      )}
                      
                      {finalizada && (
                        <div className="col-md-6 mb-2">
                          <small className="text-secondary d-block">Meio de Saída</small>
                          <strong>{movimentacao?.meio_saida?.nome || '—'}</strong>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Meio de Entrada (editável se não finalizada e canEdit) */}
                  {!finalizada && !isReadOnly && (
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-md-6">
                        <div className="p-3 border border-warning rounded h-100">
                          <h6 className="mb-3">
                            <i className="bi bi-arrow-right-circle me-2"></i>
                            Meio de Entrada
                          </h6>
                          <select
                            className="form-select"
                            value={meioEntradaId}
                            onChange={(e) => {
                              setMeioEntradaId(e.target.value);
                              handleMeioEntradaChange(e.target.value);
                            }}
                            disabled={salvandoMeioEntrada}
                          >
                            {meiosEntrada.map((me) => (
                              <option key={me.id} value={me.id}>{me.nome}</option>
                            ))}
                          </select>
                          {salvandoMeioEntrada && (
                            <small className="text-muted d-block mt-2">
                              <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                              Salvando...
                            </small>
                          )}
                          <small className="text-warning d-block mt-2">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Alterar o meio de entrada pode limpar decisões incompatíveis automaticamente.
                          </small>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="p-3 border border-secondary rounded h-100">
                          <h6 className="mb-3">Situação</h6>
                          <select
                            className="form-select"
                            value={novaSituacaoId}
                            onChange={(e) => {
                              setNovaSituacaoId(e.target.value);
                              registrarSituacao(e.target.value);
                            }}
                            disabled={registrandoSituacao}
                          >
                            {situacoes.map((s) => (
                              <option key={s.id} value={s.id}>{s.nome}</option>
                            ))}
                          </select>
                          {registrandoSituacao && (
                            <small className="text-muted d-block mt-2">
                              <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                              Salvando...
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Motivos MBA (editáveis se MBA ou Flagrante+MBA, não finalizada e canEdit) */}
                  {!finalizada && !isReadOnly && (isMBA || isFlagranteMBA) && (
                    <div className="mb-4 p-3 border border-secondary rounded">
                      <h6 className="mb-3">
                        <i className="bi bi-journal-text me-2"></i>
                        Motivos MBA
                        {motivoMbaObrigatorio && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </h6>
                      <MotivosMbaSelector
                        id="motivos_mba_ids"
                        motivos={motivosMba}
                        selectedIds={motivosMbaIds}
                        onChange={salvarMotivosMba}
                        disabled={salvandoMotivoMba}
                        required={motivoMbaObrigatorio}
                        invalid={motivoMbaObrigatorio && motivosMbaIds.length === 0}
                      />
                      {motivoMbaObrigatorio && motivosMbaIds.length === 0 && (
                        <div className="invalid-feedback d-block">
                          Motivo MBA é obrigatório para este meio de entrada
                        </div>
                      )}
                      {salvandoMotivoMba && (
                        <small className="text-muted d-block mt-2">
                          <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                          Salvando...
                        </small>
                      )}
                    </div>
                  )}

                  {/* Decisões (editável apenas para canEdit) */}
                  {!finalizada && !canViewOnly && (
                    !isFlagranteMBA ? (
                      <div className="row g-3 mb-4">
                        <div className="col-12 col-md-6">
                          <div className="p-3 border border-secondary rounded h-100">
                            <h6 className="mb-3">Decisão(ões)</h6>
                            <select
                              className="form-select"
                              value={decisaoId}
                              onChange={(e) => {
                                setDecisaoId(e.target.value);
                                salvarDecisao(e.target.value);
                              }}
                              disabled={salvandoDecisao}
                            >
                              <option value="">Selecione...</option>
                              {decisoes.map((d) => (
                                <option key={d.id} value={d.id}>{d.nome}</option>
                              ))}
                            </select>
                            {salvandoDecisao && (
                              <small className="text-muted d-block mt-2">
                                <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                                Salvando...
                              </small>
                            )}
                          </div>
                        </div>
                        {!isReadOnly && (
                          <div className="col-12 col-md-6">
                            <div className="p-3 border border-secondary rounded h-100">
                              <h6 className="mb-3">Meio de Saída</h6>
                              <select
                                className="form-select"
                                value={meioSaidaId}
                                onChange={(e) => setMeioSaidaId(e.target.value)}
                              >
                                <option value="">Selecione...</option>
                                {meiosSaida.map((m) => (
                                  <option key={m.id} value={m.id}>{m.nome}</option>
                                ))}
                              </select>
                              <small className="text-muted d-block mt-2">
                                <i className="bi bi-info-circle me-1"></i>
                                O meio de saída será salvo ao finalizar a movimentação
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4 p-3 border border-secondary rounded">
                        <h6 className="mb-3">Decisão(ões)</h6>
                        <div className="d-flex flex-column gap-3">
                          <div>
                            <label className="form-label mb-2">
                              Decisão Flagrante:
                            </label>
                            <select
                              className="form-select"
                              value={decisaoFlagranteId}
                              onChange={(e) => {
                                setDecisaoFlagranteId(e.target.value);
                                salvarDecisao(null, e.target.value, decisaoMbaId);
                              }}
                              disabled={salvandoDecisao}
                            >
                              <option value="">Selecione...</option>
                              {decisoes.map((d) => (
                                <option key={d.id} value={d.id}>{d.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="form-label mb-2">
                              Decisão MBA:
                            </label>
                            <select
                              className="form-select"
                              value={decisaoMbaId}
                              onChange={(e) => {
                                setDecisaoMbaId(e.target.value);
                                salvarDecisao(null, decisaoFlagranteId, e.target.value);
                              }}
                              disabled={salvandoDecisao}
                            >
                              <option value="">Selecione...</option>
                              {decisoes.map((d) => (
                                <option key={d.id} value={d.id}>{d.nome}</option>
                              ))}
                            </select>
                          </div>
                          {salvandoDecisao && (
                            <small className="text-muted">
                              <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                              Salvando...
                            </small>
                          )}
                        </div>
                      </div>
                    )
                  )}

                  {/* Meio de Saída (editável se não finalizada e canEdit) */}
                  {!finalizada && !isReadOnly && isFlagranteMBA && (
                    <div className="mb-4 p-3 border border-secondary rounded">
                      <h6 className="mb-3">Meio de Saída</h6>
                      <select
                        className="form-select"
                        value={meioSaidaId}
                        onChange={(e) => setMeioSaidaId(e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        {meiosSaida.map((m) => (
                          <option key={m.id} value={m.id}>{m.nome}</option>
                        ))}
                      </select>
                      <small className="text-muted d-block mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        O meio de saída será salvo ao finalizar a movimentação
                      </small>
                    </div>
                  )}

                  {/* Status dos Atendimentos */}
                  <div className="mb-4 p-3 border border-secondary rounded">
                    <h6 className="mb-3">Atendimentos</h6>
                    <div className="list-group list-group-flush">
                      {atendimentos.map((atendimento, idx) => {
                        const finalizado = Boolean(atendimento.dados?.check_saida);
                        const iniciado = Boolean(atendimento.dados?.check_entrada);
                        return (
                          <div
                            key={idx}
                            className="list-group-item bg-dark text-light border-secondary d-flex justify-content-between align-items-center"
                          >
                            <span>{atendimento.nome}</span>
                            {!atendimento.dados ? (
                              <span className="badge bg-secondary">Não iniciado</span>
                            ) : finalizado ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle-fill me-1" />
                                Finalizado
                              </span>
                            ) : iniciado ? (
                              <span className="badge bg-warning text-dark">Em andamento</span>
                            ) : (
                              <span className="badge bg-secondary">—</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Status de Finalização */}
                  {!isReadOnly && (
                    <div className="p-3 border border-secondary rounded">
                      {finalizada ? (
                      <div className="alert alert-info mb-0">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        <strong>Movimentação Finalizada</strong>
                      </div>
                    ) : podeFinalizar ? (
                      <div>
                        <div className="alert alert-warning mb-3">
                          <div className="d-flex align-items-center justify-content-center border-bottom pb-2 mb-2">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <strong>Atenção</strong>
                          </div>
                          <div>
                            Ao finalizar a Movimentação, nenhum dado poderá ser editado (Atendimentos, Situação, Decisão e Meio de Saída).
                          </div>
                        </div>
                        <button
                          className="btn btn-success w-100"
                          onClick={finalizarMovimentacao}
                          disabled={finalizando}
                        >
                          <i className="bi bi-check2-circle me-2" />
                          {finalizando ? 'Finalizando...' : 'Finalizar Movimentação'}
                        </button>
                      </div>
                    ) : (
                      <div className="alert alert-warning mb-0">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Pendências:</strong>{' '}
                        {!temDecisao && 'Decisão(ões) não informada(s). '}
                        {!meioSaidaId && 'Meio de saída não informado. '}
                        {motivoMbaObrigatorio && motivosMbaIds.length === 0 && 'Motivo MBA não informado.'}
                      </div>
                    )}
                    </div>
                  )}

                  {/* Seletor de Cor do Card */}
                  {!isReadOnly && (
                    <div className="mt-4 p-3 border border-info rounded">
                    <h6 className="mb-3">
                      <i className="bi bi-palette me-2"></i>
                      Cor de fundo do <strong>Ato</strong>
                    </h6>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {CARD_COR_OPTIONS.map(({ cor, label }) => (
                        <button
                          key={cor || 'default'}
                          type="button"
                          className="btn btn-sm position-relative"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: cor || '#2a2e32',
                            border: cardCor === cor ? '3px solid #ffc107' : '2px solid #495057',
                            borderRadius: '8px',
                          }}
                          onClick={() => {
                            setCardCor(cor);
                            salvarCardCor(cor);
                          }}
                          disabled={salvandoCor}
                          title={label}
                        >
                          {cardCor === cor && (
                            <i
                              className="bi bi-check-lg position-absolute top-50 start-50 translate-middle"
                              style={{
                                fontSize: '1.5rem',
                                color: getSwatchCheckColor(cor),
                              }}
                            ></i>
                          )}
                        </button>
                      ))}
                    </div>
                    {salvandoCor && (
                      <small className="text-muted d-block">
                        <i className="bi bi-arrow-repeat spinner-border spinner-border-sm me-1"></i>
                        Salvando cor...
                      </small>
                    )}
                    <small className="text-muted d-block">
                      Selecione uma cor para destacar o cabeçalho desta movimentação no dashboard. 
                    </small>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              {finalizada && canReopen && !isReadOnly && (
                <button
                  type="button"
                  className="btn btn-warning me-auto"
                  onClick={reabrirMovimentacao}
                  disabled={reabrindo}
                >
                  <i className="bi bi-unlock me-2" />
                  {reabrindo ? 'Reabrindo...' : 'Reabrir Movimentação'}
                </button>
              )}
              {conteudoModalCarregado && !finalizada && canDelete && (
                <button
                  type="button"
                  className="btn btn-danger me-auto"
                  onClick={abrirConfirmacaoCancelarEntrada}
                  disabled={cancelandoEntrada}
                >
                  <i className="bi bi-trash3 me-2" />
                  {cancelandoEntrada ? 'Cancelando...' : 'Cancelar Entrada'}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação para Mudança de Meio de Entrada */}
      {showConfirmMeioEntrada && (
        <div
          className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme} dashboard-themed-modal-no-overlay`}
          style={{ display: 'block', zIndex: 1060 }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  Confirmar Alteração
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  <strong>Você está prestes a alterar o meio de entrada.</strong>
                </p>
                <p className="text-warning mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  Esta ação pode limpar automaticamente as decisões que são incompatíveis com o novo meio de entrada.
                </p>
                <p className="text-muted mt-2 mb-0">
                  Deseja continuar?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelarMeioEntrada}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={confirmarMeioEntrada}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Confirmar Alteração
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Cancelar Entrada */}
      {showConfirmCancelarEntrada && (
        <div
          className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme} dashboard-themed-modal-no-overlay`}
          style={{ display: 'block', zIndex: 1060 }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: '420px' }}
            role="document"
          >
            <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
                  Confirmar cancelamento
                </h5>
              </div>
              <div className="modal-body">
                <p className="mb-0">Tem certeza que deseja excluir esta movimentação?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={fecharConfirmacaoCancelarEntrada}
                  disabled={cancelandoEntrada}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmarCancelarEntrada}
                  disabled={cancelandoEntrada}
                >
                  {cancelandoEntrada ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop para modais */}
      {(isOpen || showConfirmMeioEntrada || showConfirmCancelarEntrada) && (
        <div className={`modal-backdrop fade show dashboard-themed-backdrop dashboard-themed-modal--${theme}`}></div>
      )}
    </>
  );
}

export default MovimentacaoResumoModal;
